/**
 * GuideBookCacheService - Manages content caching and HTML extraction
 * Handles cache operations, HTML parsing, and content retrieval
 * 
 * @class GuideBookCacheService
 */
class GuideBookCacheService {
    constructor() {
        // Content cache: { "product-1_guide_getting-started": { en: {...}, pl: {...} } }
        this.contentCache = {};
    }

    /**
     * Get cache key for content lookup
     * @param {string} productId - Product ID/slug
     * @param {string} type - Content type ('guide' or 'faq')
     * @param {string} categoryId - Category ID
     * @returns {string} Cache key
     */
    getCacheKey(productId, type, categoryId) {
        return `${productId}_${type}_${categoryId}`;
    }

    /**
     * Store content in cache
     * @param {string} cacheKey - Cache key
     * @param {*} content - Content to cache
     */
    setCachedContent(cacheKey, content) {
        this.contentCache[cacheKey] = content;
    }

    /**
     * Get cached content
     * @param {string} cacheKey - Cache key
     * @returns {*} Cached content or undefined
     */
    getCachedContent(cacheKey) {
        return this.contentCache[cacheKey];
    }

    /**
     * Check if content is cached
     * @param {string} cacheKey - Cache key
     * @returns {boolean} True if content is cached
     */
    isCached(cacheKey) {
        return cacheKey in this.contentCache;
    }

    /**
     * Extract HTML from content that may be stored as JSON string or object
     * Handles multiple formats: direct HTML, JSON string with html property, object with html property
     * 
     * @param {*} content - Content to extract HTML from
     * @returns {string} Extracted HTML as string
     */
    extractHtmlContent(content) {
        // Handle undefined/null
        if (!content) {
            return '';
        }

        // If it's an object, extract the html property
        if (typeof content === 'object' && content !== null) {
            const html = content.html;
            // If html property exists, ensure it's a string
            if (html !== undefined && html !== null) {
                return String(html);
            }
            return '';
        }

        // If it's a string, try to parse it as JSON first
        if (typeof content === 'string') {
            try {
                const parsed = JSON.parse(content);
                if (typeof parsed === 'object' && parsed !== null && parsed.html) {
                    return String(parsed.html);
                }
                // If parsed but no html property, return the original string
                return content;
            } catch (e) {
                // If JSON parsing fails, assume it's direct HTML and return as-is
                return content;
            }
        }

        // Fallback: ensure we always return a string
        return String(content);
    }

    /**
     * Strip HTML tags and extract plain text for searching
     * Useful for full-text search across content
     * 
     * @param {string} html - HTML content
     * @returns {string} Plain text
     */
    stripHtmlTags(html) {
        if (!html) return '';
        if (typeof html !== 'string') return '';
        // Remove HTML tags and decode entities
        return html
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Get content by language from cached data
     * @param {*} cachedData - Cached content
     * @param {string} lang - Language code (e.g., 'en', 'pl')
     * @returns {string} Content HTML as string
     */
    getContentByLanguage(cachedData, lang) {
        // We already have HTML extraction built-in; just delegate
        let content = cachedData[lang] || cachedData;
        return this.extractHtmlContent(content);
    }

    /**
     * Clear all cached content
     */
    clearCache() {
        this.contentCache = {};
    }

    /**
     * Pre-populate cache with content
     * @param {string} productId - Product ID
     * @param {Object} guideContent - Guide content mapping
     * @param {Object} faqContent - FAQ content mapping
     */
    populateCache(productId, guideContent = {}, faqContent = {}) {
        // Cache all guide content
        for (const guideId of Object.keys(guideContent)) {
            const cacheKey = this.getCacheKey(productId, 'guide', guideId);
            if (!this.isCached(cacheKey)) {
                this.setCachedContent(cacheKey, guideContent[guideId]);
            }
        }

        // Cache all FAQ content
        for (const faqId of Object.keys(faqContent)) {
            const cacheKey = this.getCacheKey(productId, 'faq', faqId);
            if (!this.isCached(cacheKey)) {
                this.setCachedContent(cacheKey, faqContent[faqId]);
            }
        }
    }
}

export default GuideBookCacheService;
